<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-credit-note-main">
    <div class="credit-note-options">
      <Input :inputValue.sync="attnInputValue" placeholder="Attn To." validator="text,150" />
      <div class="cn-filters-and-table-box-controls">
        <div class="cn-filters-summary-container">
          <span>{{ cnFiltersSummary }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="cnFiltersIsOpen"
        >
          <div class="button-filter-content-container">
            <BoxContainer title="Credit Note Date" :isCheckBox="true" :isChecked.sync="cnDateIsChecked">
              <DateTimePicker
                placeholder="Start CN Date "
                :value.sync="cnDateStartValue"
                :showTime="false"
                :isRequired="cnDateIsChecked"
                :errorMessage.sync="cnDateStartErrorMessage"
                :isDisabled="!cnDateIsChecked"
              />
              <DateTimePicker
                placeholder="End CN Date "
                :value.sync="cnDateEndValue"
                :showTime="false"
                :isRequired="cnDateIsChecked"
                :errorMessage.sync="cnDateEndErrorMessage"
                :isDisabled="!cnDateIsChecked"
              />
            </BoxContainer>
            <BoxContainer
              class="cnno-filter-container"
              title="Credit Note No."
              :isCheckBox="true"
              :isChecked.sync="cnnoIsChecked"
            >
              <InputQuery
                placeholder="Start CN No."
                :items="cnnoItems"
                itemKey="Credit No"
                :showDesc="false"
                :selectedObject.sync="cnnoStartSelectedObject"
                :search.sync="cnnoStartSearch"
                :isRequired="cnnoIsChecked"
                :errorMessage.sync="cnnoStartErrorMessage"
                :isDisabled="!cnnoIsChecked"
              />
              <InputQuery
                placeholder="End CN No."
                :items="cnnoItems"
                itemKey="Credit No"
                :showDesc="false"
                :selectedObject.sync="cnnoEndSelectedObject"
                :search.sync="cnnoEndSearch"
                :isRequired="cnnoIsChecked"
                :errorMessage.sync="cnnoEndErrorMessage"
                :isDisabled="!cnnoIsChecked"
              />
            </BoxContainer>
            <BoxContainer
              class="customer-filter-container"
              title="Customer"
              :isCheckBox="true"
              :isChecked.sync="customerIsChecked"
            >
              <InputQuery
                placeholder="Start Customer"
                :items="customerItems"
                itemKey="Customer Code"
                :showDesc="false"
                :selectedObject.sync="customerStartSelectedObject"
                :isRequired="customerIsChecked"
                :errorMessage.sync="customerStartErrorMessage"
                :isDisabled="!customerIsChecked"
              />
              <InputQuery
                placeholder="End Customer"
                :items="customerItems"
                itemKey="Customer Code"
                :showDesc="false"
                :selectedObject.sync="customerEndSelectedObject"
                :isRequired="customerIsChecked"
                :errorMessage.sync="customerEndErrorMessage"
                :isDisabled="!customerIsChecked"
              />
            </BoxContainer>
            <div class="not-printed-filter">
              <CheckBoxV2 title="Show Only Not Printed" :isChecked.sync="showOnlyNotPrintedIsChecked" />
            </div>
            <div class="refresh-button-container">
              <button @click="refreshClicked">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
        <button
          class="select-deselect-all-button"
          :isDisabled="selectDeselectAllButtonIsDisabled"
          @click="selectDeselectAllClicked"
        >
          {{ selectDeselectAllButtonText }}
        </button>
      </div>
      <div class="cn-table-box-container">
        <TableBox
          title="Credit Note"
          :tableData.sync="cnTableData"
          :selectedRow.sync="cnSelectedRow"
          :maxSelection="cnTableData.length"
          :dataType="cnDataType"
          :loading="cnLoading"
          :hideColumns="hiddenCol"
          :columnWidth="[120, 130, 150]"
        />
      </div>
      <div class="container-and-currency-formatting-controls">
        <Dropdown
          class="container-list-dropdown"
          placeholder="Container List"
          :items="containerListItems"
          itemKey="name"
          :selectedObject.sync="containerListSelectedObject"
          :isRequired="true"
          :errorMessage.sync="containerListErrorMessage"
          :isDisabled="cnControlsDisabled"
        />
        <Dropdown
          class="currency-format-dropdown"
          placeholder="Currency Format"
          :items="currencyFormatItems"
          itemKey="name"
          :selectedObject.sync="currencyFormatSelectedObject"
          :isRequired="true"
          :errorMessage.sync="currencyFormatErrorMessage"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
      </div>
      <div class="delivery-type-and-currency-formatting-controls">
        <Dropdown
          class="delivery-type-dropdown"
          placeholder="Delivery Type"
          :items="deliveryTypeItems"
          itemKey="Description"
          :selectedObject.sync="deliveryTypeSelectedObject"
          :isDisabled="cnControlsDisabled"
        />
      </div>
      <div class="header-formatting-controls">
        <div class="header-formatting-column">
          <CheckBoxV2
            title="Show Company Letter Head"
            :isChecked.sync="showCompanyLetterHeadIsChecked"
            :isDisabled="cnControlsDisabled"
          />
          <CheckBoxV2
            title="Show Header Info"
            :isChecked.sync="showHeaderInfoIsChecked"
            :isDisabled="cnControlsDisabled"
          />
        </div>
        <div class="header-formatting-column">
          <CheckBoxV2
            v-if="showInternalCopyOnlyCheckbox"
            title="Internal Copy Only"
            :isChecked.sync="internalCopyOnlyIsChecked"
            :isDisabled="cnControlsDisabled"
          />
        </div>
      </div>
      <div class="cn-format-controls">
        <Dropdown
          class="format-dropdown"
          placeholder="Format"
          :items="formatItems"
          itemKey="name"
          :selectedObject.sync="formatSelectedObject"
          :isRequired="true"
          :errorMessage.sync="formatErrorMessage"
          :isDisabled="cnControlsDisabled"
        />
      </div>
      <div class="cn-footer-controls">
        <InputQuery
          class="footer-dropdown"
          placeholder="Footer Image"
          :items="footerImageItems"
          itemKey="description"
          descKey="name"
          :showDesc="true"
          :selectedObject.sync="footerImageSelectedObject"
          :isRequired="true"
          :errorMessage.sync="footerImageErrorMessage"
          :isDisabled="cnControlsDisabled"
        />
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewCNClicked" :disabled="cnControlsDisabled">Preview Credit Note</button>
      </div>
    </div>
    <div class="credit-note-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        :downloadName="downloadFileName"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span>Select some credit note and click on <strong>Preview Credit Note</strong> to get started</span>
      </div>
    </div>
    <PopupV2
      class="email-cn-popup"
      :show="showEmailCNPopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailCNPopupButtons"
      title="Email Credit Note"
      width="60%"
      @buttonClicked="emailCNPopupButtonClicked"
    >
      <div class="email-cn-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailCNPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailCNPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailCNPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailCNPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailCNPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    selectDeselectAllButtonIsDisabled: function () {
      return this.cnTableData.length == 0;
    },

    selectDeselectAllButtonText: function () {
      if (this.cnTableData.length == 0) {
        return 'Select All';
      }
      return this.cnTableData.length != this.cnSelectedRow.length ? 'Select All' : 'Deselect All';
    },
  },
  data: function () {
    return {
      hiddenCol: [],
      userDetails: null,
      printedBySelection: '0',
      settingsSystemCoFooterFin2: '',
      settingsSystemCoFooterFin3: '',

      attnInputValue: '',

      //#region CredNote Filters
      cnFiltersSummary: '',
      cnFiltersIsOpen: false,

      cnDateIsChecked: false,
      cnDateStartValue: null,
      cnDateStartErrorMessage: '',
      cnDateEndValue: null,
      cnDateEndErrorMessage: '',

      cnnoIsChecked: false,
      cnnoItems: [],
      cnnoStartSelectedObject: null,
      cnnoStartSearch: '',
      cnnoStartErrorMessage: '',
      cnnoEndSelectedObject: null,
      cnnoEndSearch: '',
      cnnoEndErrorMessage: '',

      customerIsChecked: false,
      customerStartSelectedObject: null,
      customerStartErrorMessage: '',
      customerEndSelectedObject: null,
      customerEndErrorMessage: '',
      customerItems: [],

      showOnlyNotPrintedIsChecked: false,
      //#endregion

      //#region cn table box
      cnTableData: [],
      cnSelectedRow: [],
      cnDataType: { Total: 'currency' },
      cnLoading: false,
      cnControlsDisabled: false,
      //#endregion

      //#region container list
      containerListItems: [
        { name: 'All Container related to Job', APIValue: 'job' },
        { name: 'All Container related to Credit Note', APIValue: 'credit' },
        { name: 'None', APIValue: '' },
      ],
      containerListSelectedObject: null,
      containerListErrorMessage: '',
      currencyFormatItems: [
        { name: 'Original Currency', APIValue: 'original' },
        { name: 'Local Currency', APIValue: 'local' }, // =============== hardcoded for now
      ],
      currencyFormatSelectedObject: null,
      currencyFormatErrorMessage: '',
      //#endregion

      //#region Delivery Type
      deliveryTypeItems: [],
      deliveryTypeSelectedObject: null,
      //#endregion

      showCompanyLetterHeadIsChecked: false,
      showHeaderInfoIsChecked: false,

      showInternalCopyOnlyCheckbox: false,
      internalCopyOnlyIsChecked: false,

      //#region formats
      formatItems: [
        // hardcoded on the front-end for now
        { name: 'Non-Tax', APIValue: '0' },
        { name: 'Tax', APIValue: '1' },
        { name: 'Default', APIValue: '2' },
      ],
      formatSelectedObject: null,
      formatErrorMessage: '',
      //#endregion

      //#region footer Image
      footerImageItems: [
        { name: 'Image 1', description: '' },
        { name: 'Image 2', description: '' },
        { name: 'Image 3', description: '' },
      ],
      footerImageSelectedObject: null,
      footerImageErrorMessage: '',
      //#endregion

      //#region designerv3
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },
      downloadFileName: '',
      //#endregion

      cnSignatureRequired: false,
      cnTaxExemptionFormat: '0',
      cnRemarksLoc: '0',
      cnShowPrintDate: true,
      cnShowPreparedBy: true,
      cnShowCreditTerm: false,

      //#region email CN
      showEmailCNPopup: false,
      emailCNPopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailCNPopupToMails: [],
      emailCNPopupCCMails: [],
      emailCNPopupBCCMails: [],
      emailCNPopupSubjectInputValue: '',
      emailCNPopupMessageText: '',
      //#endregion
    };
  },
  props: {},
  methods: {
    refreshClicked: async function () {
      let error = false;

      if (this.cnDateIsChecked) {
        if (!this.cnDateStartValue) {
          this.cnDateStartErrorMessage = 'Start CN Date is required!';
          error = true;
        }

        if (!this.cnDateEndValue) {
          this.cnDateEndErrorMessage = 'End CN Date is required!';
          error = true;
        }

        // if (this.cnDateStartValue && this.cnDateEndValue && (this.cnDateStartValue > this.cnDateEndValue)) {
        //   this.cnDateStartErrorMessage = "Start CN Date cannot be greater than End CN Date";
        //   this.cnDateEndErrorMessage = "End CN Date cannot be smaller than Start CN Date";
        //   error = true;
        // }
      }

      if (this.cnnoIsChecked) {
        if (!this.cnnoStartSelectedObject && !this.cnnoStartSearch.trim()) {
          this.cnnoStartErrorMessage = 'Start CN No. is required!';
          error = true;
        }

        if (!this.cnnoEndSelectedObject && !this.cnnoEndSearch.trim()) {
          this.cnnoEndErrorMessage = 'End CN No. is required!';
          error = true;
        }

        // if (this.cnnoStartSelectedObject && this.cnnoEndSelectedObject) {
        //   let cnnoStartDate = new Date(this.cnnoStartSelectedObject['Date']);
        //   let cnnoEndDate = new Date(this.cnnoEndSelectedObject['Date']);
        //   if (cnnoStartDate > cnnoEndDate) {
        //     this.cnnoStartErrorMessage = "Start CN No. cannot be greater than End CN No.";
        //     this.cnnoEndErrorMessage = "End CN No. cannot be smaller than Start CN No.";
        //     error = true;
        //   }
        // }
      }

      if (this.customerIsChecked) {
        if (!this.customerStartSelectedObject) {
          this.customerStartErrorMessage = 'Start Customer is required!';
          error = true;
        }

        if (!this.customerEndSelectedObject) {
          this.customerEndErrorMessage = 'End Customer is required!';
          error = true;
        }
      }

      if (error) return;

      if (!this.cnDateIsChecked && !this.cnnoIsChecked) {
        const proceedConfirmation = await this.$showAlert(
          'warning',
          'This May Take A While',
          'You are not filtering by CN Date and CN No.\n\nLoading this data may take a while. Proceed?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedConfirmation === 'Cancel') return;
      }

      this.cnFiltersIsOpen = false;
      this.updateCNFilterSummary();
      await this.getFilteredCNList();
    },

    updateCNFilterSummary: function () {
      const filtersList = [];

      if (this.cnDateIsChecked) {
        filtersList.push(
          `date from ${this.cnDateStartValue.formatString('yyyy-MM-dd')} to ${this.cnDateEndValue.formatString('yyyy-MM-dd')}`,
        );
      }

      if (this.cnnoIsChecked) {
        let cnnoFilterSummary = 'CN No. from ';

        if (this.cnnoStartSelectedObject) {
          cnnoFilterSummary += `${this.cnnoStartSelectedObject['Credit No']} to `;
        } else {
          cnnoFilterSummary += `${this.cnnoStartSearch} to `;
        }

        if (this.cnnoEndSelectedObject) {
          cnnoFilterSummary += `${this.cnnoEndSelectedObject['Credit No']}`;
        } else {
          cnnoFilterSummary += `${this.cnnoEndSearch}`;
        }

        filtersList.push(cnnoFilterSummary);
      }

      if (this.customerIsChecked) {
        filtersList.push(
          `customer from ${this.customerStartSelectedObject['Customer Code']} to ${this.customerEndSelectedObject['Customer Code']}`,
        );
      }

      if (this.showOnlyNotPrintedIsChecked) {
        filtersList.push(`credit notes not printed yet`);
      }

      this.cnFiltersSummary = `Filter by ${filtersList.join(', ')}`;
    },

    getFilteredCNList: async function () {
      const requestBody = {
        txntype: '',
        startdate: this.cnDateIsChecked ? this.cnDateStartValue.formatString('yyyy-MM-dd') : '',
        enddate: this.cnDateIsChecked ? this.cnDateEndValue.formatString('yyyy-MM-dd') : '',
        startcnno:
          this.cnnoIsChecked ?
            this.cnnoStartSelectedObject ?
              this.cnnoStartSelectedObject['Credit No']
            : this.cnnoStartSearch
          : '',
        endcnno:
          this.cnnoIsChecked ?
            this.cnnoEndSelectedObject ?
              this.cnnoEndSelectedObject['Credit No']
            : this.cnnoEndSearch
          : '',
        startcustomer: this.customerIsChecked ? this.customerStartSelectedObject['Customer Code'] : '',
        endcustomer: this.customerIsChecked ? this.customerEndSelectedObject['Customer Code'] : '',
        notprinted: this.showOnlyNotPrintedIsChecked ? '1' : '0',
      };
      this.cnTableData = [];
      this.cnSelectedRow = [];

      this.cnLoading = true;
      // Because reusing the same getcreditlisting as Credit page, so need filter out 'C' credit notes so it wont affect Credit Not page
      const creditListing = await this.$apiCallV2('/PrintCreditNote/GetCreditListing', requestBody);
      this.cnTableData = creditListing.filter((cl) => cl.Indicator !== 'C');
      this.cnLoading = false;
    },

    selectDeselectAllClicked: function () {
      if (this.cnTableData.length !== this.cnSelectedRow.length) {
        this.cnSelectedRow = [];
        this.$nextTick(() => {
          this.cnSelectedRow = [...this.cnTableData];
        });
      } else {
        this.cnSelectedRow = [];
      }
    },

    previewCNClicked: async function () {
      if (this.cnSelectedRow.length == 0) {
        this.$showAlert('error', 'No Credit Note Selected', 'Please select at least one credit note to preview', [
          { text: 'OK' },
        ]);
        return;
      }

      let error = false;

      if (!this.containerListSelectedObject) {
        this.containerListErrorMessage = 'Container List is required';
        error = true;
      }

      if (!this.currencyFormatSelectedObject) {
        this.currencyFormatErrorMessage = 'Currency Format is required!';
        error = true;
      }

      if (!this.formatSelectedObject) {
        this.formatErrorMessage = 'Format is required!';
        error = true;
      }

      if (error) return;
      const requestBody = {
        attnto: this.attnInputValue,
        cnnoarray: this.cnSelectedRow.map((cnSelectedRow) => cnSelectedRow['Credit No']).join(','),
        containeroption: this.containerListSelectedObject.APIValue,
        deliverytype: this.deliveryTypeSelectedObject?.['Delivery Type']?.trim?.() ?? '',
        currency: this.currencyFormatSelectedObject.APIValue,
        showcompanyletterhead: this.showCompanyLetterHeadIsChecked ? '1' : '0',
        showheaderinfo: this.showHeaderInfoIsChecked ? '1' : '0',
        txntype: '0',
        company: this.userDetails.CoName,
        coshortname: this.userDetails.CoRefname,
        gstformat: this.formatSelectedObject.APIValue,
        doctxn: '3',
      };

      this.$fullscreenLoading('Getting Credit Note Details');
      let cnDetails = await this.$apiCallV2('/PrintCreditNote/GetPrintCreditDetails', requestBody);
      if (Array.isArray(cnDetails) && cnDetails[0].Results === '1') {
        this.$showAlert('error', 'Error Printing Credit Note', cnDetails[0].ErrMsg, [{ text: 'OK' }]);
        this.$fullscreenLoading();
        return;
      }
      cnDetails = cnDetails.PrintCN;

      // these are needed for any format
      cnDetails.General['Show Company Letter Head'] = cnDetails.General['Show Company Letter Head'] !== '0';
      cnDetails.General['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : cnDetails.General['companyheader'];
      cnDetails.General._companyName = this.userDetails.CoName;
      cnDetails.General._signatureRequired = this.cnSignatureRequired;
      // cnDetails.General.companyfooter = this.userDetails.CoFooterFin;
      switch (this.footerImageSelectedObject.name) {
        case 'Image 1':
          cnDetails.General.companyfooter = this.userDetails.CoFooterFin;
          break;

        case 'Image 2':
          cnDetails.General.companyfooter = this.settingsSystemCoFooterFin2;
          break;

        case 'Image 3':
          cnDetails.General.companyfooter = this.settingsSystemCoFooterFin3;
          break;
      }
      // cnDetails.General._printdatetime = new Date().formatString('dd/MM/yyyy HH:mm');

      this.$fullscreenLoading('Preparing to Print');
      for (const cn of cnDetails.Credit) {
        if (cn.QRURL != '') {
          cn.QRImage = await this.$getQRURL(cn.QRURL, { size: 150, margin: 10 });
        }
        const addr1 = cn.cu_addr1;
        const addr2 = cn.cu_addr2;
        const addr3 = cn.cu_addr3.trim().length > 0 ? cn.cu_addr3 : `${cn.postcode} ${cn.city}`;
        const addr4 =
          cn.cu_addr4.trim().length > 0 ? cn.cu_addr4
          : cn.cu_addr3.trim().length > 0 ? `${cn.postcode} ${cn.city}`
          : `${cn.state} ${cn.country}`;
        const addr5 =
          cn.cu_addr4.trim().length > 0 ? `${cn.postcode} ${cn.city}<br>${cn.state} ${cn.country}`
          : cn.cu_addr3.trim().length > 0 ? `${cn.state} ${cn.country}`
          : '';

        const telfax = `Tel: ${cn.cu_phone}   Fax: ${cn.cu_fax}`;
        const att = this.attnInputValue.trim() || cn.cu_contact?.trim() !== '';
        const taxDetails = `BRN\t: ${cn.BRN ?? ''}<br>SST No\t: ${cn.SSTNo ?? ''}<br>TIN No\t: ${cn.TINNo ?? ''}`;

        let formattedCreditTerm = '';
        if (cn.CreditType === 'C') {
          formattedCreditTerm = 'C.O.D';
        } else if (this.cnShowCreditTerm && cn.CreditTerm && cn.CreditTerm > 0) {
          formattedCreditTerm = `${cn.CreditTerm} ${cn.CreditType === 'D' ? 'DAY' : 'MONTH'}${cn.CreditTerm > 1 ? 'S' : ''}`;
        }

        const leftValueArray = []; //store the header left side rows
        const rightValueObjectArray = []; //store the header right side rows
        leftValueArray.push(
          cn.cu_name + '<br>',
          addr1,
          addr2,
          addr3,
          addr4 + (addr5.trim() != '' ? `<br>${addr5}` : ''),
          telfax,
          att ?
            `Attn: ${cn.cu_contact?.trim() !== '' ? cn.cu_contact : this.attnInputValue.trim()}<br>${taxDetails}`
          : taxDetails,
        );
        rightValueObjectArray.push(
          {
            rightLabel: 'No.',
            rightValue: `<b>${cn.credit_no}<b>`,
          },
          {
            rightLabel: 'Date',
            rightValue: new Date(cn.Date).formatString('dd/MM/yyyy'),
          },
          {
            rightLabel: 'A/C No.',
            rightValue: cn.billcu_no,
          },
        );
        if (formattedCreditTerm) {
          rightValueObjectArray.push({
            rightLabel: 'Terms',
            rightValue: formattedCreditTerm,
          });
        }
        rightValueObjectArray.push(
          {
            rightLabel: 'Job No.',
            rightValue: cn.jobno,
          },
          {
            rightLabel: 'Page',
            rightValue: 'Page {{jsreportPageNum}} of {{jsreportTotalPage}}',
          },
        );
        if (this.cnShowPrintDate) {
          rightValueObjectArray.push({
            rightLabel: 'Print Date',
            rightValue: new Date().formatString('dd/MM/yyyy HH:mm'),
          });
        }
        if (this.cnShowPreparedBy) {
          const preparedByValue = this.printedBySelection == '0' ? this.userDetails.UserName : cn.UserID;
          rightValueObjectArray.push({
            rightLabel: 'Prepared By',
            rightValue: preparedByValue,
          });
        }

        //get the highest length from the left and right array
        const loopCount = Math.max(leftValueArray.length, rightValueObjectArray.length);
        cn._cnHeader1 = []; //initialize the array in the json
        for (let l = 0; l < loopCount; l++) {
          //loop with the loopCount then get the value and push as object into the array
          cn._cnHeader1.push({
            leftValue: l < leftValueArray.length ? leftValueArray[l] : '',
            rightLabel: l < rightValueObjectArray.length ? rightValueObjectArray[l]['rightLabel'] : '',
            c: l < rightValueObjectArray.length ? ':' : '', //for hiding the colon if rightValueObject dont have value
            rightValue: l < rightValueObjectArray.length ? rightValueObjectArray[l]['rightValue'] : '',
          });
        }

        if (cn.remarks.trim() || cn.remarks2 || cn.remarks3) {
          const remarks =
            cn.remarks + (cn.remarks2 ? `\n${cn.remarks2}` : '') + (cn.remarks3 ? `\n${cn.remarks3}` : '');
          cn._jobRemarks = [
            {
              remarksLabel: 'Remarks',
              remarksValue: remarks,
            },
          ];

          switch (this.cnRemarksLoc) {
            // case '1':
            //   break;

            case '2':
              cn._showChargesListRemarks = true;
              break;

            case '3':
              cn._showGrandTotalRemarks = true;
              break;

            default:
              cn._showHeaderRemarks = true;
              break;
          }
        }

        if (cnDetails.General['Show Header Info'] !== '0') {
          switch (cn.jobsystem.toUpperCase()) {
            case 'HMS':
              cn._jobHeader1 = [
                {
                  leftLabel: 'Request No.',
                  leftValue: cn.requestno,
                  rightLabel: 'Shipper/Consignee',
                  rightValue: cn.shipcon,
                },
                {
                  leftLabel: 'Request Date',
                  leftValue: new Date(cn.requestdate).formatString('dd/MM/yyyy'),
                  rightLabel: 'Forwarding Agent',
                  rightValue: cn.cu_sname,
                },
              ];

              cn._jobHeader2 = [
                {
                  leftLabel: 'CDA No.',
                  leftValue: cn.consignmentno,
                },
                {
                  leftLabel: 'From/To',
                  leftValue: `${cn.deptcode} [${cn.deptcham}] / ${cn.destcode} [${cn.destcham}]`,
                },
                {
                  leftLabel: 'Commodity',
                  leftValue: cn.Commodity,
                },
              ];
              break;

            case 'FMS':
              cn._jobHeader1 = [
                {
                  leftLabel: cn.vslTitle ?? 'Vessel/Voy No.',
                  leftValue: cn.vessel ?? '',
                  rightLabel: 'Origin/Dest.',
                  rightValue: cn.port ?? '',
                },
                {
                  leftLabel: cn.etatitle ?? 'ETA',
                  leftValue: new Date(cn.eta).formatString('dd/MM/yyyy'),
                  rightLabel: 'Shipper',
                  rightValue: cn.shipper,
                },
                {
                  leftLabel: cn.blTitle ?? 'B/L No.',
                  leftValue: cn.blno,
                  rightLabel: 'Consignee',
                  rightValue: cn.consgn,
                },
              ];

              cn._jobHeader2 = [
                {
                  leftLabel: 'GWeight/Meas',
                  // leftValue: `${this.$weightFormat(cn.gweight)} KG / ${cn.meas ? Number(cn.meas).toFixed(3) : '0.000'} M3`,
                  leftValue: `${this.$weightFormat(cn.gweight)} KG / ${cn.meas ? this.$volumeFormat(cn.meas) : this.$volumeFormat(0) } M3`,
                },
                {
                  leftLabel: 'Commodity',
                  leftValue: cn.goodsdesc,
                },
              ];
              break;

            case 'FRE':
              cn._jobHeader1 = [
                {
                  leftLabel: 'Mawb/OBL No',
                  leftValue: cn.jobblno,
                  rightLabel: 'Origin/Dest',
                  rightValue: cn.fromTo,
                },
                {
                  leftLabel: 'Hawb/HBL No',
                  leftValue: cn.hblno ?? '',
                  rightLabel: 'Weight/CBM',
                  rightValue: `${this.$weightFormat(cn.gweight)} KG / ${this.$volumeFormat(cn.meas)} M3`,
                },
                {
                  leftLabel: 'Fit/Vsl/Date',
                  leftValue: cn.fitVslDate,
                  rightLabel: 'Commodity',
                  rightValue: cn.pkgcommodity,
                },
              ];

              cn._jobHeader2 = [
                {
                  leftLabel: 'Shipper',
                  leftValue: cn.shipper,
                },
                {
                  leftLabel: 'Consignee',
                  leftValue: cn.consgn,
                },
              ];
          }

          if (cn.Credit_Cont && cn.Credit_Cont.length > 0) {
            const contList = cn.Credit_Cont;
            cn._jobContainer = [
              {
                contLabel: 'Container No.',
                containers: contList.map((cont) => `${cont.cont_no} (${cont.cont_size}')`).join('   '),
                totalCont: `(${cn.totalCont})`,
              },
            ];
          }

          if (cn.invJobNo) {
            cn._jobAdditional = [
              {
                jobinfoleft: cn.invJobLeft ? cn.invJobLeft : '',
                jobinforight: cn.invJobRight ? cn.invJobRight : '',
              },
            ];
          }
        }

        if (cn.Credit_Charges) {
          if (
            this.formatSelectedObject.name === 'Tax' ||
            (this.formatSelectedObject.name === 'Default' && cn.GSTEffective === '1')
          ) {
            cn.charges = this.formatCNChargesForGeneralTaxCNTemplate(cn.Credit_Charges);

            cn.totalAmtExclTax = 0;
            cn.totalTaxAmt = 0;
            cn.totalFAmtInclTax = 0;
            cn.totalAmtInclTax = 0;

            for (const chrg of cn.charges) {
              cn.totalAmtExclTax += Number(chrg.amtExclTax.replaceAll(',', ''));
              cn.totalTaxAmt += Number(chrg.taxAmt.replaceAll(',', ''));
              cn.totalFAmtInclTax += Number(chrg.famtInclTax.replaceAll(',', ''));
              cn.totalAmtInclTax += Number(chrg.amtInclTax.replaceAll(',', ''));
            }

            cn.totalAmtExclTax = this.$currencyFormat(cn.totalAmtExclTax);
            cn.totalTaxAmt = this.$currencyFormat(cn.totalTaxAmt);
            cn.totalFAmtInclTax = this.$currencyFormat(cn.totalFAmtInclTax);
            cn.totalAmtInclTax = this.$currencyFormat(cn.totalAmtInclTax);
            cn.grandTotalInclTax = cn.totalFAmtInclTax;
          } else if (
            this.formatSelectedObject.name === 'Non-Tax' ||
            (this.formatSelectedObject.name === 'Default' && cn.GSTEffective === '0')
          ) {
            cn.charges = this.formatCNChargesForGeneralNonTaxCNTemplate(cn.Credit_Charges);

            cn.charges.totalAmt = 0;
            for (const chrg of cn.charges) {
              cn.charges.totalAmt += Number(chrg.amt.replaceAll(',', ''));
            }
            cn.totalAmt = this.$currencyFormat(cn.charges.totalAmt.toString());

            const gst = this.formatGSTForGeneralNonTaxCNTemplate(cn.Credit_Charges);
            if (gst.length > 0) {
              cn._gst = gst;
            }

            let totalTax = 0;

            if (cn._gst) {
              cn._gst.forEach((gst) => {
                totalTax += Number(gst.cnTaxAmt);
              });
            }
            const grandtotal = Number(cn.totalAmt.replaceAll(',', '')) + Number(totalTax);
            cn.grandTotalInclTax = this.$currencyFormat(grandtotal.toString());
          }

          const chargesTax = cn.Credit_Charges.map((chrg) => chrg['TaxCode'].substring(0, 3));
          if (chargesTax.includes('ESV')) {
            cnDetails.General._showTextualTaxExemptionFormat = true;
            cnDetails.General._showTabularTaxExemption = false;
            cn._formattedSumGSTTaxExempted = this.$currencyFormat(cn.sumGSTTaxExempted);
            cn._formattedTotalInclExemptedGST = this.$currencyFormat(cn.TotalInclExemptedGST);
          } else {
            cnDetails.General._showTextualTaxExemptionFormat = false;
            cnDetails.General._showTabularTaxExemption = false;
          }
        }

        cn._downloadFileName = `${cn.credit_no}_${new Date().formatString('yyyyMMddHHmm')}`;
      }
      this.$fullscreenLoading();

      switch (this.formatSelectedObject.name) {
        case 'Non-Tax':
          this.designerV3ReportFiles = Array.from({ length: cnDetails.Credit.length }, () => 'NonTaxCreditNote.json');
          break;

        case 'Tax':
          this.designerV3ReportFiles = cnDetails.Credit.map((cn) =>
            cn.currency == 'MYR' || this.currencyFormatSelectedObject['APIValue'] === 'local' ? 'TaxCreditNote.json' : 'TaxCreditNoteForeignCurrency.json',
          );
          break;

        case 'Default':
          this.designerV3ReportFiles = cnDetails.Credit.map((cn) =>
            cn.GSTEffective === '1' ?
              cn.currency === 'MYR' || this.currencyFormatSelectedObject['APIValue'] === 'local' ?
                'TaxCreditNote.json'
              : 'TaxCreditNoteForeignCurrency.json'
            : 'NonTaxCreditNote.json',
          );
          break;
      }

      // Set file name for download
      this.downloadFileName = `${cnDetails.Credit.length == 1 ? cnDetails.Credit[0].credit_no : cnDetails.Credit[0].credit_no}_${new Date().formatString('yyyyMMddHHmm')}`;
      this.designerV3ReportData = cnDetails.Credit;
      this.designerV3SharedData = cnDetails.General;
      this.showDesignerV3 = true;
    },

    formatCNChargesForGeneralTaxCNTemplate: function (cnCharges) {
      const allCharges = [];
      for (const cn of cnCharges) {
        const combineRemark = `${cn.Remark}${cn.Remark && cn.Remark2 ? `<br>${cn.Remark2}` : cn.Remark2}${(cn.Remark || cn.Remark2) && cn.Remark3 ? `<br>${cn.Remark3}` : cn.Remark3}`;
        const formattedCnChrg = {
          description:
            cn.description.trim().length > 0 ?
              `${cn.description}${combineRemark ? `<br>${combineRemark}` : combineRemark}`
            : combineRemark,
          job_no: cn.jobno,
          invno: cn.invno,
          invdate: new Date(cn.invdate).formatString('dd/MM/yyyy') ?? '',
          amtExclTax: this.$currencyFormat(cn.CreditFAmount),
          taxAmt: this.$currencyFormat(cn.taxAmt),
          famtInclTax: this.$currencyFormat(cn.NetFAmount),
          amtInclTax: this.$currencyFormat(cn['Net Amount']),
        };

        const tax = Number(cn.Tax).toLocaleString('fullwide', { useGrouping: false });
        if (cn.TaxCode) {
          formattedCnChrg.taxrate = `${cn.TaxCode}@${tax}%`;
        } else {
          formattedCnChrg.taxrate = `${tax}%`;
        }

        allCharges.push(formattedCnChrg);
      }

      return allCharges;
    },

    formatCNChargesForGeneralNonTaxCNTemplate: function (cnCharges) {
      const allCharges = [];
      for (const [index, cn] of cnCharges.entries()) {
        const tax = Number(cn.Tax).toLocaleString('fullwide', { useGrouping: false });
        const combineRemark = `${cn.Remark}${cn.Remark && cn.Remark2 ? `<br>${cn.Remark2}` : cn.Remark2}${(cn.Remark || cn.Remark2) && cn.Remark3 ? `<br>${cn.Remark3}` : cn.Remark3}`;
        const formattedCnChrg = {
          itemNo: index + 1,
          description:
            cn.description.trim().length > 0 ?
              `${cn.description}${combineRemark ? `<br>${combineRemark}` : combineRemark}`
            : combineRemark,
          job_no: cn.jobno,
          invno: cn.invno,
          invdate: new Date(cn.invdate).formatString('dd/MM/yyyy') ?? '',
          amt: this.$currencyFormat(cn.CreditFAmount),
          tax: tax,
        };
        allCharges.push(formattedCnChrg);
      }

      return allCharges;
    },

    formatGSTForGeneralNonTaxCNTemplate: function (cnCharges) {
      const gstGroups = [];
      const uniqueTax = [...new Set(cnCharges.map((cn) => cn.Tax))];

      if (uniqueTax.length == 0 || uniqueTax.every((tax) => tax == '0')) return [];
      uniqueTax.forEach((tax) => {
        if (tax != '0') {
          let taxdesc = '';
          if (tax == '5' && ['HIF EXPRESS', 'HIF LOGISTICS'].includes(this.userDetails.CoRefname)) {
            taxdesc = 'MISC CHARGES';
          } else {
            taxdesc = `${tax}% GOVERNMENT SERVICE TAX`;
          }
          gstGroups.push({
            cnTaxDescription: taxdesc,
            cnTaxAmt: 0,
          });
        }
      });

      for (const cn of cnCharges) {
        if (cn.Tax == '5' && ['HIF EXPRESS', 'HIF LOGISTICS'].includes(this.userDetails.CoRefname)) {
          gstGroups
            .filter((gst) => gst.cnTaxDescription === 'MISC CHARGES')
            .forEach((gst) => (gst.cnTaxAmt += Number(cn.taxAmt)));
        } else {
          gstGroups
            .filter((gst) => gst.cnTaxDescription.substring(0, 1) === cn.Tax)
            .forEach((gst) => (gst.cnTaxAmt += Number(cn.taxAmt)));
        }
      }

      gstGroups.forEach((gst) => {
        gst.cnTaxAmt = this.$currencyFormat(gst.cnTaxAmt);
      });
      return gstGroups;
    },

    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].cu_email) {
        this.emailCNPopupToMails = this.designerV3ReportData[0].cu_email.split(';').map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailCNPopupToMails = [];
      }

      this.emailCNPopupCCMails = [];
      this.emailCNPopupBCCMails = [];

      if (this.designerV3ReportData.length > 1) {
        this.emailCNPopupSubjectInputValue = 'Credit Notes';
        this.emailCNPopupMessageText = `Credit Note: ${shareLink}\n\n`;

        for (const cnData of this.designerV3ReportData) {
          this.emailCNPopupMessageText += `Credit Note ${cnData.credit_no}\n`;
          const cnDocumentsRoute = this.$router.resolve(
            `/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${cnData.credit_no}`,
          );
          this.emailCNPopupMessageText += `Documents: ${window.location.origin}${cnDocumentsRoute.href}\n\n`;
        }
      } else {
        this.emailCNPopupSubjectInputValue = `Credit Note ${this.designerV3ReportData[0].credit_no}`;
        this.emailCNPopupMessageText = `Credit Note: ${shareLink}\n`;

        const cnDocumentsRoute = this.$router.resolve(
          `/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${this.designerV3ReportData[0].credit_no}`,
        );
        this.emailCNPopupMessageText += `Documents: ${window.location.origin}${cnDocumentsRoute.href}\n`;
      }

      this.showEmailCNPopup = true;
    },

    designerV3AfterPrint: async function () {
      const requestBody = {
        report: 'INVOICE',
        coshortname: this.userDetails.CoRefname,
        internalcopy: this.internalCopyOnlyIsChecked ? '1' : '0',
        invoices: this.designerV3ReportData.map((reportData) => ({
          recordid: reportData.credit_no,
          invtype: 'CN',
        })),
      };

      const response = await this.$apiCallV2('/PrintCreditNote/UpdatePrintCount', requestBody);
      if (response !== '') {
        this.$showAlert(
          'error',
          'Failed to update print count',
          response?.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },

    emailCNPopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailCNPopup = false;
        return;
      }

      if (this.emailCNPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Emails', 'Please enter at least one valid To email', [{ text: 'OK' }]);
        return;
      }

      if (!this.emailCNPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );
        if (proceedWithoutSubject === 'Cancel') {
          return;
        }
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'CreditNote',
            docno: this.designerV3ReportData.map((cnData) => cnData.creditno).join(','),
            docnofieldname: '',
            recipient: this.emailCNPopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailCNPopupCCMails.map((mail) => mail.email).join(';'),
            bcc: this.emailCNPopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailCNPopupSubjectInputValue.trim(),
            message: this.emailCNPopupMessageText,
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PrintCreditNote/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailInvoicePopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },

  async mounted() {
    this.cnLoading = true;
    this.cnControlsDisabled = true;
    this.hiddenCol = [
      'Customer Name',
      'Date',
      'Balance',
      'Currency',
      'FTotal',
      'Indicator',
      'Authorized',
      'Remarks',
      'Created By',
      'Created Date',
      'Updated by',
      'Updated Date',
      'Ack. Letter No'
    ];
    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-180);
    startDate = startDate.formatString('yyyy-MM-dd');
    const endDate = currentDate.formatString('yyyy-MM-dd');
    const dataToPost = {
      startdate: startDate,
      enddate: endDate,
      txntype: '0',
    };

    let mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.$apiCallV2(`/PrintCreditNote/GetCreditListing`, dataToPost).then((result) => {
        this.cnnoItems = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CUSTOMER' }).then((result) => {
        this.customerItems = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTDELIVERYTYPE' }).then((result) => {
        this.deliveryTypeItems = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTDELIVERYTYPE' }).then((result) => {
        this.deliveryTypeItems = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_InvInternalCopy').then((result) => {
        // show the checkboxes if the returned value is truthy
        const settingsSystemFINInvInternalCopy = result[0].SettingsValue?.toUpperCase?.() ?? '';
        this.showInternalCopyOnlyCheckbox =
          settingsSystemFINInvInternalCopy === 'TRUE' || settingsSystemFINInvInternalCopy === 'Y';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=Sign_CN').then((result) => {
        // =========== needs to change based on what document is being printed
        const settingsSystemSignIN = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemSignIN) {
          this.cnSignatureRequired = settingsSystemSignIN === 'TRUE' || settingsSystemSignIN === 'Y';
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_InvTaxExptFormat').then((result) => {
        // =========== may need to change based on what document is being printed
        const settingsSystemInvTaxExptFormat = result[0].SettingsValue ?? '';
        if (settingsSystemInvTaxExptFormat) {
          this.cnTaxExemptionFormat = settingsSystemInvTaxExptFormat;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_InvGSTFormat').then((result) => {
        const finInvGSTFormat = result[0].SettingsValue;
        const matchingFormat = this.formatItems.find((formatItem) => formatItem.APIValue === finInvGSTFormat);
        if (matchingFormat) {
          this.formatSelectedObject = matchingFormat;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LetterHead_CN').then((result) => {
        const letterHead = result[0].SettingsValue?.toUpperCase();
        if (letterHead) {
          this.showCompanyLetterHeadIsChecked = letterHead == 'TRUE';
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin2').then((result) => {
        this.settingsSystemCoFooterFin2 = result[0].SettingsValue ?? '';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin3').then((result) => {
        this.settingsSystemCoFooterFin3 = result[0].SettingsValue ?? '';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    const footerImageDescriptionPromises = [
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc1').then((result) => {
        const image1 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 1');
        image1.description = result[0].SettingsValue || image1.name;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc2').then((result) => {
        const image2 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 2');
        image2.description = result[0].SettingsValue || image2.name;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc3').then((result) => {
        const image3 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 3');
        image3.description = result[0].SettingsValue || image3.name;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    ];
    Promise.allSettled(footerImageDescriptionPromises).then((_promiseOutcomes) => {
      // only populate footer image selection after all descriptions have been loaded, otherwise description will be empty
      this.footerImageSelectedObject = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 1');
    });
    mountedPromises = mountedPromises.concat(footerImageDescriptionPromises); // footer image description promises also need to be fulfilled before invoices can be previewed

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=JobInfo_CN').then((result) => {
        const headerInfo = result[0].SettingsValue;
        if (headerInfo) {
          this.showHeaderInfoIsChecked = headerInfo == 'TRUE';
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GCNRptEnclosureLoc').then((result) => {
        const settingsSystemCnRemarksLoc = result[0].SettingsValue ?? '0';
        if (settingsSystemCnRemarksLoc) {
          this.cnRemarksLoc = settingsSystemCnRemarksLoc;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_CNShowPrintDate').then((result) => {
        const settingsSystemCnShowPrintDate = result[0].SettingsValue ?? '1'; //default is show
        if (settingsSystemCnShowPrintDate == '0') {
          this.cnShowPrintDate = false;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_CNShowPreparedBy').then((result) => {
        const settingsSystemCnShowPreparedBy = result[0].SettingsValue ?? '1'; //default is show
        if (settingsSystemCnShowPreparedBy == '0') {
          this.cnShowPreparedBy = false;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_CNShowCreditTerm').then((result) => {
        const settingsSystemCnShowCreditTerm = result[0].SettingsValue ?? '0'; //default is hide
        if (settingsSystemCnShowCreditTerm == '1') {
          this.cnShowCreditTerm = true;
        }
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_RptPrintedBy').then((result) => {
        this.printedBySelection = result[0].SettingsValue ?? '0';
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    if (this.$route.query.cnno) {
      this.cnnoIsChecked = true;

      const queryParamCNNo = this.$route.query.cnno.trim();
      const matchingCN = this.cnnoItems.find((cn) => cn['Credit No'] === queryParamCNNo);

      if (matchingCN) {
        this.cnnoStartSelectedObject = matchingCN;
        this.cnnoEndSelectedObject = matchingCN;
      } else {
        this.cnnoStartSearch = queryParamCNNo;
        this.cnnoEndSearch = queryParamCNNo;
      }
    } else {
      this.cnDateIsChecked = true;
      this.cnDateStartValue = new Date();
      this.cnDateEndValue = new Date();
    }

    this.updateCNFilterSummary();
    await this.getFilteredCNList();

    await Promise.allSettled(mountedPromises).then(() => {
      this.cnLoading = false;
      this.cnControlsDisabled = false;
    });

    this.containerListSelectedObject = this.containerListItems.find(
      (containerList) => containerList.name === 'All Container related to Job',
    );

    // =============== check if this default changes based on company or settingssystem
    this.currencyFormatSelectedObject = this.currencyFormatItems.find(
      (currencyFormat) => currencyFormat.name === 'Original Currency',
    );

    this.cnSelectedRow = this.$route.query.cnno ? [...this.cnTableData] : [];
  },
  watch: {
    cnDateIsChecked: function (val) {
      if (!val) {
        this.cnDateStartErrorMessage = '';
        this.cnDateEndErrorMessage = '';
      }
    },

    cnnoIsChecked: function (val) {
      if (!val) {
        this.cnnoStartErrorMessage = '';
        this.cnnoEndErrorMessage = '';
      }
    },

    customerIsChecked: function (val) {
      if (!val) {
        this.customerStartErrorMessage = '';
        this.customerEndErrorMessage = '';
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/css/all.scss';

.print-credit-note-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .credit-note-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .cn-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .cn-filters-summary-container {
        margin-right: 5px;
        align-content: center; // vertically align the summary text
        text-align: end; // horizontally align the summary text
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .cnno-filter-container {
          margin-top: 15px;
        }

        .customer-filter-container {
          margin-top: 15px;
        }

        .not-printed-filter {
          margin-top: 5px;
          display: flex;
          justify-content: flex-start;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .cn-table-box-container {
      height: calc(100% - 345px);
    }

    > .container-and-currency-formatting-controls {
      margin-top: 5px;
      display: flex;
      flex-direction: row;

      > .container-list-dropdown {
        margin-right: 5px;
      }

      > .currency-format-dropdown {
        margin-left: 5px;
      }
    }

    > .delivery-type-and-currency-formatting-controls {
      display: flex;
      flex-direction: row;

      > .delivery-type-dropdown {
        margin-right: 5px;
      }
    }

    > .credit-term-and-pay-to-formatting-controls {
      display: flex;
      flex-direction: row;

      > .credit-term-dropdown {
        margin-right: 5px;
        width: calc(50% - 5px);
      }

      > .pay-to-dropdown {
        width: calc(50% - 5px);
      }
    }

    > .header-formatting-controls {
      display: flex;
      flex-direction: row;
      margin-top: 5px;

      > .header-formatting-column {
        width: 50%;
      }
    }

    > .cn-format-controls {
      margin-top: 5px;

      > .format-dropdown {
        width: calc(50% - 5px);
      }
    }

    > .cn-footer-controls {
      margin-top: 5px;

      > .footer-dropdown {
        width: calc(50% - 5px);
      }
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  > .credit-note-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-cn-popup {
    .email-cn-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
